"use client"
import React, { useState } from "react"
import { t } from "@/translations"
import { subscribeKlaviyoEmail } from "@/actions/klaviyo/subscribeEmail"
import { match } from "ts-pattern"
import KlaviyoInputField from "./KlaviyoInputField"
import ButtonPill from "../../../Molecules/ButtonPill"
import toast from "react-hot-toast"
import { CentraLocaleShippingCountryCode } from "@/data/centra-locale-shipping-country-code"
import { getKlaviyoListId } from "./helper"
import getKlaviyoProfile from "@/actions/klaviyo/getKlaviyoProfile"
import { updateKlaviyoProfile } from "@/actions/klaviyo/updateKlaviyoProfile"

interface KlaviyoSignupFormProps {
    langCode: string
    countryCode: CentraLocaleShippingCountryCode
}

export default function KlaviyoSignupForm({
    langCode,
    countryCode
}: KlaviyoSignupFormProps) {
    const [isSignedUp, setIsSignedUp] = useState<boolean | undefined>(undefined)
    const [email, setEmail] = useState<string>("")
    const [loading, setLoading] = useState<boolean>(false)

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        // When a user are subscribing to the newsletter, We are doing the following:

        // 1. The email is subscribed to the newsletter
        // 2. We then retreive the Klaviyo profile ID based on the email
        // 3. We update the language in the Klaviyo profile

        e.preventDefault()
        setLoading(true)
        const email = (
            e.currentTarget.querySelector('input[type="email"]') as HTMLInputElement
        ).value

        const klaviyoListId = getKlaviyoListId(countryCode)

        const subscribeKlaviyoEmailResult = await subscribeKlaviyoEmail(
            email,
            klaviyoListId
        )

        match(subscribeKlaviyoEmailResult)
            .with(
                {
                    success: true
                },
                async () => {
                    setIsSignedUp(true)

                    setEmail("")
                    setIsSignedUp(false)
                    setLoading(false)

                    // get ID based on email
                    const klaviyoProfile = await getKlaviyoProfile(email)

                    let parsedKlaviyoProfile
                    if (typeof klaviyoProfile === "string") {
                        // Parse if it's a JSON string
                        parsedKlaviyoProfile = JSON.parse(klaviyoProfile)
                    } else {
                        // Use it directly if it's already an object
                        parsedKlaviyoProfile = klaviyoProfile
                    }

                    const klaviyoId = parsedKlaviyoProfile?.data?.[0]?.id

                    if (klaviyoId && klaviyoId.length > 0) {
                        // update language in Klaviyo profile
                        await updateKlaviyoProfile(klaviyoId, langCode)
                    }

                    toast.success(t(langCode, "newsletter-subscription-success"), {
                        duration: 4000,
                        position: "bottom-left"
                    })
                }
            )
            .with(
                {
                    success: false
                },
                () => {
                    setIsSignedUp(false)
                    toast.error(t(langCode, "newsletter-subscription-failed"), {
                        duration: 4000,
                        position: "bottom-left"
                    })
                    setLoading(false)
                }
            )

        setLoading(false)
        setTimeout(() => {
            setIsSignedUp(undefined)
        }, 3000)
    }

    return (
        <form
            className="mb-48 flex flex-col gap-12 text-stone-400"
            onSubmit={handleSubmit}
        >
            <KlaviyoInputField
                placeholder={t(langCode, "adam.smith@email.com")}
                id="id"
                label={t(langCode, "Your email")}
                theme="dark"
                single="true"
                type="email"
                success={isSignedUp}
                value={email}
                onChange={e => setEmail(e.target.value)}
                loading={loading}
            />
            <ButtonPill color="white" size="medium" variant="primary">
                {t(langCode, "Sign up now")}
            </ButtonPill>
        </form>
    )
}
